import { useState, useEffect } from "react";
import { useTranslation } from 'react-i18next';
import { notifyError } from '@config/tostifyConfig/tostifyConfig';
import {
    extractErrorMessage,
    shouldBlockFetchError,
    showBlockingError,
} from '@shared/services/blockingError';

const useFetchData = () => {
    const { t } = useTranslation();
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pageNumber, setpageNumber] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedData, setSelectedData] = useState(null);
    const [sortBy, setSortBy] = useState('');
    const [sortDir, setSortDir] = useState('asc');
    const [pageSize, setPageSize] = useState(10);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    const [searchQuery, setSearchQuery] = useState({});
    const [searchType, setSearchType] = useState('');

    const fetchData = async (getData, params = {}, options = {}) => {
        try {
            setLoading(true);
            setError(null);

            const requestParams = {
                pageNumber,
                pageSize,
                sortBy: params.sortBy || sortBy,
                sortDir: params.sortDir || sortDir,
                searchQuery: params.searchQuery || searchQuery,
                ...params
            };

            const response = await getData(requestParams);

            if (response && response.content) {
                setData(response.content);
                const totalElements = response.page?.totalElements || response.totalElements || 0;
                setTotalElements(totalElements);
                setTotalPages(Math.ceil(totalElements / pageSize) || 0);
            } else {
                setData([]);
                setTotalElements(0);
                setTotalPages(0);
            }
        } catch (error) {
            console.error('Error fetching/searching users:', error);

            if (shouldBlockFetchError(error, options)) {
                showBlockingError(extractErrorMessage(error));
                setError(null);
            } else {
                setError(error?.response?.data?.message || error?.message || 'Failed to fetch data');
            }

            setData([]);
            setTotalElements(0);
            setTotalPages(0);
            throw error;
        } finally {
            setLoading(false);
        }
    };

    const handleSearchClick = (getData, params = {}, options = {}) => {
        setpageNumber(0);
        return fetchData(getData, {
            ...params,
            pageNumber: 0
        }, options);
    };

    const handleCheckboxChange = (index) => {
        const dataId = data[index].id;
        setSelectedData((prevSelectedData) => (prevSelectedData === dataId ? null : dataId));
    };

    const handleSort = (column, getData) => {
        let newSortDir;

        const sortColumn = column || 'name';  // Default to 'name' if column is empty

        if (sortBy === sortColumn) {
            newSortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
            newSortDir = sortDir === 'asc' ? 'desc' : 'asc';
            setSortBy(sortColumn);
        }
        setSortDir(newSortDir);
        setpageNumber(0);
    };

    const handleError = (message) => {
        notifyError(message);
    };

    return {
        // State values
        data,
        loading,
        error,
        pageNumber,
        totalPages,
        totalElements,
        selectedData,
        sortBy,
        sortDir,
        pageSize,
        showDeleteConfirmation,
        searchQuery,
        searchType,

        // Setters
        setData,
        setLoading,
        setError,
        setpageNumber,
        setTotalPages,
        setTotalElements,
        setSelectedData,
        setSortBy,
        setSortDir,
        setPageSize,
        setShowDeleteConfirmation,
        setSearchQuery,
        setSearchType,

        // Handlers
        fetchData,
        handleCheckboxChange,
        handleSort,
        handleSearchClick,
        handleError,
    };
};

export default useFetchData;